import { useAtomValue } from 'jotai'
import React from 'react'

import { ExtensionInstance } from '@mangovoice/types'

import { extensionsListAtom } from '../../atoms'
import type { SwitchboardTranslateFn } from '../../types'
import { text } from '../../utils/text'
import { NodeSelect } from './NodeSelect'

export interface QueueSelectorProps {
  /** Current selected queue ID */
  value?: string
  /** Callback when queue selection changes */
  onChange: (queueId: string | undefined) => void
  /** Placeholder text for the select */
  placeholder?: string
  /** Label for the floating label */
  label?: string
  /** Optional translation function for i18n */
  t?: SwitchboardTranslateFn
  // Note: extensionsList is provided via Jotai atoms (extensionsListAtom)
  // Use useSwitchboardDataAtoms hook in the consuming app to set atoms
}

/**
 * Reusable queue selector dropdown component.
 * Automatically filters to only show Call Queue extensions (ExtensionCallQueue type).
 *
 * Data Source: ExtensionCallQueue types from extensionsListAtom (Jotai atom)
 * Fetched via: GET /app/v1/extensions/?pbx_id={id}&type=ExtensionCallQueue
 * Documentation: libraries/switchboard-ui/NODE_DATA_POPULATION/queue.md
 * Component: QueueNode uses QueueSelector
 */
export const QueueSelector: React.FC<QueueSelectorProps> = ({
  value = '',
  onChange,
  placeholder: placeholderProp,
  label: labelProp,
  t,
}) => {
  const placeholder =
    placeholderProp ??
    text(t, 'switchboard.core.selectQueue', 'Select queue...')
  const label = labelProp ?? text(t, 'switchboard.core.queueLabel', 'Queue')
  const extensionsList = useAtomValue(extensionsListAtom)
  const availableQueues = React.useMemo(() => {
    if (!extensionsList) return []
    return extensionsList.filter((ext) => {
      if (ext.type !== 'ExtensionCallQueue') return false
      const extWithActive = ext as ExtensionInstance & { active?: boolean }
      if ('active' in extWithActive) {
        return extWithActive.active === true
      }
      return true
    })
  }, [extensionsList])

  const selectRef = React.useRef<HTMLSelectElement>(null)

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(e.target.value || undefined)
    },
    [onChange],
  )

  const handleMouseDown = React.useCallback(
    (_e: React.MouseEvent<HTMLSelectElement>) => {
      // Focus the select if it's not already focused
      if (selectRef.current && document.activeElement !== selectRef.current) {
        selectRef.current.focus()
      }
    },
    [],
  )

  return (
    <NodeSelect
      ref={selectRef}
      label={label}
      value={value}
      onChange={handleChange}
      onMouseDown={handleMouseDown}
    >
      <option value="">{placeholder}</option>
      {availableQueues.map((queue) => (
        <option key={queue.id} value={String(queue.id)}>
          {queue.number} -{' '}
          {queue.name || text(t, 'switchboard.core.unnamed', 'Unnamed')}
        </option>
      ))}
    </NodeSelect>
  )
}
